import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Role } from "../types";
import { API_BASE, parseJsonOrThrow } from "../api/base";

const OAUTH_CLIENT_ID = import.meta.env.VITE_OAUTH_CLIENT_ID ?? "orderflow-spa";
const SESSION_STORAGE_KEY = "orderflow.auth.session";
const MIN_STORED_TOKEN_TTL_MS = 5_000;

// Refresh this many seconds before the access token actually expires, so
// a normal page session never hits a hard 401 from clock skew or network
// latency at the exact expiry instant.
const REFRESH_SKEW_SECONDS = 60;

interface AuthState {
  idToken: string | null;
  roles: Role[];
  email: string | null;
  mfaEnrolled: boolean;
}

interface PendingLogin {
  email: string;
  password: string;
  codeVerifier: string;
  codeChallenge: string;
}

interface MfaEnrollment {
  provisioningUri: string;
  secret: string;
}

interface AuthContextValue extends AuthState {
  loading: boolean;
  mfaPending: boolean;
  mfaEnrollment: MfaEnrollment | null;
  /** Resolves on success; throws MfaRequiredError if a TOTP code is
   * needed next (caller should prompt for it and call submitMfaCode). */
  login(email: string, password: string): Promise<void>;
  submitMfaCode(code: string): Promise<void>;
  /** Called once mfaEnrolled is false — fetches a fresh TOTP secret/QR
   * URI and stores it in mfaEnrollment for the enrollment screen to
   * render. */
  startMfaEnrollment(): Promise<void>;
  verifyMfaEnrollment(code: string): Promise<void>;
  logout(): Promise<void>;
}

export class MfaRequiredError extends Error {}

interface TokenResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: "Bearer";
  email: string;
  roles: Role[];
  mfaEnrolled: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface StoredTokenResponse extends TokenResponse {
  expiresAt: number;
}

function isRole(value: unknown): value is Role {
  return value === "Nurse" || value === "Doctor" || value === "Admin";
}

function readStoredTokenResponse(): StoredTokenResponse | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredTokenResponse>;
    if (
      typeof parsed.accessToken !== "string" ||
      typeof parsed.email !== "string" ||
      !Array.isArray(parsed.roles) ||
      !parsed.roles.every(isRole) ||
      typeof parsed.mfaEnrolled !== "boolean" ||
      typeof parsed.expiresAt !== "number" ||
      parsed.expiresAt - Date.now() < MIN_STORED_TOKEN_TTL_MS
    ) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }

    return {
      accessToken: parsed.accessToken,
      expiresIn: Math.max(0, Math.floor((parsed.expiresAt - Date.now()) / 1000)),
      tokenType: "Bearer",
      email: parsed.email,
      roles: parsed.roles,
      mfaEnrolled: parsed.mfaEnrolled,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    return null;
  }
}

function writeStoredTokenResponse(tokens: TokenResponse): void {
  try {
    const stored: StoredTokenResponse = {
      ...tokens,
      expiresAt: Date.now() + tokens.expiresIn * 1000,
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Session storage is a UX cache only; the HttpOnly refresh cookie is authoritative.
  }
}

function clearStoredTokenResponse(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function generatePkcePair(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const verifierBytes = new Uint8Array(32);
  crypto.getRandomValues(verifierBytes);
  const codeVerifier = base64UrlEncode(verifierBytes);

  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
  const codeChallenge = base64UrlEncode(new Uint8Array(digest));

  return { codeVerifier, codeChallenge };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [initialTokens] = useState(() => readStoredTokenResponse());
  const [state, setState] = useState<AuthState>(() =>
    initialTokens
      ? {
          idToken: initialTokens.accessToken,
          roles: initialTokens.roles,
          email: initialTokens.email,
          mfaEnrolled: initialTokens.mfaEnrolled,
        }
      : {
          idToken: null,
          roles: [],
          email: null,
          mfaEnrolled: false,
        },
  );
  const [pendingLogin, setPendingLogin] = useState<PendingLogin | null>(null);
  const [mfaEnrollment, setMfaEnrollment] = useState<MfaEnrollment | null>(null);
  const [loading, setLoading] = useState(initialTokens === null);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshInFlight = useRef<Promise<void> | null>(null);
  const mfaEnrollInFlight = useRef<Promise<void> | null>(null);
  const idTokenRef = useRef<string | null>(initialTokens?.accessToken ?? null);
  // Holds the latest silentRefresh implementation so the setTimeout inside
  // applyTokenResponse can call it without a circular useCallback
  // dependency (applyTokenResponse schedules silentRefresh; silentRefresh
  // calls applyTokenResponse on success) — this ref is just how that cycle
  // gets broken, not a caching mechanism.
  const silentRefreshRef = useRef<() => Promise<void>>(async () => {});

  const scheduleRefresh = useCallback((expiresIn: number) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    const delayMs = Math.max(5, expiresIn - REFRESH_SKEW_SECONDS) * 1000;
    refreshTimer.current = setTimeout(() => {
      void silentRefreshRef.current();
    }, delayMs);
  }, []);

  const clearSession = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
    mfaEnrollInFlight.current = null;
    idTokenRef.current = null;
    clearStoredTokenResponse();
    setMfaEnrollment(null);
    setState({ idToken: null, roles: [], email: null, mfaEnrolled: false });
  }, []);

  const applyTokenResponse = useCallback((tokens: TokenResponse) => {
    writeStoredTokenResponse(tokens);
    idTokenRef.current = tokens.accessToken;
    setState({
      idToken: tokens.accessToken,
      roles: tokens.roles,
      email: tokens.email,
      mfaEnrolled: tokens.mfaEnrolled,
    });
    scheduleRefresh(tokens.expiresIn);
  }, [scheduleRefresh]);

  const silentRefresh = useCallback((opts?: { clearOnFailure?: boolean }) => {
    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }

    const refresh = (async () => {
      try {
        const res = await fetch(`${API_BASE}/oauth/token`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ grantType: "refresh_token", clientId: OAUTH_CLIENT_ID }),
        });
        const body = await parseJsonOrThrow(res);
        applyTokenResponse(body as TokenResponse);
      } catch {
        if (opts?.clearOnFailure === false) return;
        // No valid refresh cookie (first visit, expired session, or a
        // revoked/reused token) — this just means "not logged in", not an
        // error worth surfacing.
        clearSession();
      }
    })();

    refreshInFlight.current = refresh;
    refresh.finally(() => {
      if (refreshInFlight.current === refresh) {
        refreshInFlight.current = null;
      }
    });
    return refresh;
  }, [applyTokenResponse, clearSession]);

  useEffect(() => {
    silentRefreshRef.current = silentRefresh;
  }, [silentRefresh]);

  // Restore a session across page reloads. The old Cognito-based context
  // had no persistence at all (memory-only, lost on every refresh) — this
  // silent-refresh-on-mount is a genuine improvement, not just parity,
  // made possible by the refresh token now living in a cookie the browser
  // keeps automatically instead of only in JS memory.
  useEffect(() => {
    if (initialTokens) {
      scheduleRefresh(initialTokens.expiresIn);
    }
    silentRefresh({ clearOnFailure: initialTokens === null }).finally(() => setLoading(false));
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
    // Deliberately runs once on mount only — silentRefresh itself is
    // stable (its own deps are stable across renders), so this isn't
    // missing anything by omitting it from the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exchangeCode = useCallback(
    async (code: string, codeVerifier: string) => {
      const tokenRes = await fetch(`${API_BASE}/oauth/token`, {
        method: "POST",
        credentials: "include", // receives the Set-Cookie refresh token
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grantType: "authorization_code",
          clientId: OAUTH_CLIENT_ID,
          code,
          codeVerifier,
        }),
      });
      const tokens = (await parseJsonOrThrow(tokenRes)) as TokenResponse;
      applyTokenResponse(tokens);
    },
    [applyTokenResponse],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      try {
        const { codeVerifier, codeChallenge } = await generatePkcePair();

        const authorizeRes = await fetch(`${API_BASE}/oauth/authorize`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            password,
            codeChallenge,
            codeChallengeMethod: "S256",
            clientId: OAUTH_CLIENT_ID,
          }),
        });
        const authorizeBody = (await parseJsonOrThrow(authorizeRes)) as
          | { mfaRequired: true }
          | { code: string };

        if ("mfaRequired" in authorizeBody) {
          setPendingLogin({ email, password, codeVerifier, codeChallenge });
          throw new MfaRequiredError("TOTP code required");
        }

        await exchangeCode(authorizeBody.code, codeVerifier);
        setPendingLogin(null);
      } finally {
        setLoading(false);
      }
    },
    [exchangeCode],
  );

  const submitMfaCode = useCallback(
    async (code: string) => {
      if (!pendingLogin) throw new Error("No login in progress");
      setLoading(true);
      try {
        const authorizeRes = await fetch(`${API_BASE}/oauth/authorize`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: pendingLogin.email,
            password: pendingLogin.password,
            mfaCode: code,
            codeChallenge: pendingLogin.codeChallenge,
            codeChallengeMethod: "S256",
            clientId: OAUTH_CLIENT_ID,
          }),
        });
        const authorizeBody = (await parseJsonOrThrow(authorizeRes)) as { code: string };
        await exchangeCode(authorizeBody.code, pendingLogin.codeVerifier);
        setPendingLogin(null);
      } finally {
        setLoading(false);
      }
    },
    [pendingLogin, exchangeCode],
  );

  const startMfaEnrollment = useCallback(() => {
    if (mfaEnrollment) {
      return Promise.resolve();
    }
    if (mfaEnrollInFlight.current) {
      return mfaEnrollInFlight.current;
    }
    if (!state.idToken) {
      return Promise.reject(new Error("Not authenticated"));
    }

    const token = state.idToken;
    const enroll = (async () => {
      const res = await fetch(`${API_BASE}/oauth/mfa/enroll`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = (await parseJsonOrThrow(res)) as MfaEnrollment;
      if (idTokenRef.current === token) {
        setMfaEnrollment(body);
      }
    })();

    mfaEnrollInFlight.current = enroll;
    const clearInFlight = () => {
      if (mfaEnrollInFlight.current === enroll) {
        mfaEnrollInFlight.current = null;
      }
    };
    enroll.then(clearInFlight, clearInFlight);
    return enroll;
  }, [state.idToken, mfaEnrollment]);

  const verifyMfaEnrollment = useCallback(
    async (code: string) => {
      if (!state.idToken) throw new Error("Not authenticated");
      const res = await fetch(`${API_BASE}/oauth/mfa/verify`, {
        method: "POST",
        credentials: "include", // rotates the refresh cookie alongside the new access token
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${state.idToken}`,
        },
        body: JSON.stringify({ code }),
      });
      const tokens = (await parseJsonOrThrow(res)) as TokenResponse;
      applyTokenResponse(tokens);
      setMfaEnrollment(null);
    },
    [state.idToken, applyTokenResponse],
  );

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/oauth/revoke`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
    } finally {
      clearSession();
      setPendingLogin(null);
      setMfaEnrollment(null);
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      loading,
      mfaPending: pendingLogin !== null,
      mfaEnrollment,
      login,
      submitMfaCode,
      startMfaEnrollment,
      verifyMfaEnrollment,
      logout,
    }),
    [
      state,
      loading,
      pendingLogin,
      mfaEnrollment,
      login,
      submitMfaCode,
      startMfaEnrollment,
      verifyMfaEnrollment,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
